import React, { useState, useEffect } from "react";
import {
  getHotels,
  resetPassword,
  changeHotelStatus,
  autoLogin,
} from "../utils/helperFunctions";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import {
  MdAdd,
  MdEdit,
  MdLockReset,
  MdFilterAlt,
  MdClear,
  MdBusiness,
  MdPhone,
  MdCloudUpload,
  MdLogin,
  MdAddCard,
} from "react-icons/md";
import { NavLink, useNavigate } from "react-router-dom";
import HotelFormModal from "../components/HotelFormModal";

const HotelList = ({ onLogin }) => {
  const [hotels, setHotels] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hotelEditFormData, setHotelEditFormData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    hotelName: "",
    mobileNumber: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedManager, setselectedManager] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [filters]);

  useEffect(() => {
    fetchHotels();
  }, [debouncedFilters]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && showResetModal) {
        event.preventDefault();
        setShowResetModal(false);
      } else if (event.key === "Enter" && showResetModal) {
        event.preventDefault();
        setShowResetModal(false);
        handleResetPassword(selectedManager._id);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showResetModal]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFilters = () => {
    if (filters.hotelName || filters.mobileNumber) {
      setFilters({
        hotelName: "",
        mobileNumber: "",
      });
    }
  };

  const fetchHotels = async () => {
    try {
      setIsLoading(true);
      const params = {
        ...(debouncedFilters.hotelName.trim() && {
          hotelName: debouncedFilters.hotelName,
        }),
        ...(debouncedFilters.mobileNumber.trim() && {
          mobileNumber: debouncedFilters.mobileNumber,
        }),
      };
      const response = await getHotels(params);
      if (response.status) {
        setHotels(response.hotels);
      }
    } catch (error) {
      console.error("Failed to fetch hotels:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (managerId) => {
    try {
      const response = await resetPassword(managerId);
      toast.success(response.data.msg || "Password reset successfully");
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error(error.response?.data?.msg || "Failed to reset password");
    }
  };

  const toggleHotelStatus = async (hotel, newStatus) => {
    try {
      const response = await changeHotelStatus(hotel._id, newStatus);

      if (response.data.status) {
        toast.success(
          `Hotel ${newStatus ? "Activated" : "Deactivated"} successfully`
        );
        fetchHotels();
      }
    } catch (error) {
      console.error("Error updating hotel status:", error);
      toast.error("Failed to update hotel status");
    }
  };

  // autoLogin
  const handleInPersonLogin = async (userId) => {
    try {
      const response = await autoLogin(userId);

      if (response.status) {
        onLogin(
          response.role,
          response.token,
          response.refreshToken,
          response.name,
          response.isResetPassword
        );
        toast.success(response.msg);
      }
    } catch (error) {
      console.error("Error updating hotel status:", error);
      toast.error("Failed to update hotel status");
    }
  };

  const openEditModal = (hotel) => {
    setIsEditing(true);
    setHotelEditFormData({
      hotel: {
        hotelName: hotel.hotelName || "",
        location: hotel.location || "",
        city: hotel.city || "",
        state: hotel.state || "",
        country: hotel.country || "",
        mobileNumber: hotel.mobileNumber || "",
        pathikPassword: "",
        pathikUsername: hotel.pathikUsername || "",
        _id: hotel._id,
      },
      manager: hotel.manager
        ? {
            name: hotel.manager.name || "",
            email: hotel.manager.email || "",
            mobile: hotel.manager.mobile || "",
            password: "",
            role: hotel.manager.role || "manager",
            _id: hotel.manager._id,
          }
        : null,
      documents: hotel.manager?.documents || [],
    });
    setShowModal(true);
  };

  const openAddModal = () => {
    setIsEditing(false);
    setHotelEditFormData(null);
    setShowModal(true);
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50">
      <div className="container mx-auto mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Hotel Management
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage all hotels and their managers
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setShowFilters(!showFilters);
                  resetFilters();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors shadow-sm"
              >
                {showFilters ? (
                  <MdClear className="text-lg" />
                ) : (
                  <MdFilterAlt className="text-lg" />
                )}
                {showFilters ? "Hide Filters" : "Filters"}
              </button>

              <button
                onClick={openAddModal}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <MdAdd className="text-lg" />
                Add Hotel
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-4 rounded-xl shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Total Hotels
                  </p>
                  <p className="text-2xl font-bold mt-1">{hotels.length}</p>
                </div>
                <div className="bg-indigo-100 p-3 rounded-full">
                  <svg
                    className="h-6 w-6 text-indigo-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-4 rounded-xl shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Recently Added (7 days)
                  </p>
                  <p className="text-2xl font-bold mt-1">
                    {
                      hotels.filter(
                        (h) =>
                          new Date(h.createdAt) >
                          new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                      ).length
                    }
                  </p>
                </div>
                <div className="bg-purple-100 p-3 rounded-full">
                  <svg
                    className="h-6 w-6 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>

          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: "auto" }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.3 }}
              className="bg-white p-6 rounded-lg shadow-sm border border-indigo-300"
            >
              <div className="flex justify-end mb-4">
                {Object.values(filters).some(
                  (val) => val !== "" && val !== null
                ) && (
                  <button
                    onClick={resetFilters}
                    className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    <MdClear className="text-lg" />
                    Clear all
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-indigo-500">
                    Hotel Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MdBusiness className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="hotelName"
                      value={filters.hotelName}
                      onChange={handleFilterChange}
                      placeholder="Search by hotel name"
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-medium text-indigo-500">
                    Contact Number
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MdPhone className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="mobileNumber"
                      value={filters.mobileNumber}
                      onChange={handleFilterChange}
                      placeholder="Search by contact number"
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:border-indigo-500 text-sm"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        <div className="mx-auto bg-white shadow-sm overflow-hidden">
          <div className="p-4 md:p-6">
            {isLoading && hotels.length === 0 ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            ) : hotels.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                <p className="mt-4 text-lg font-medium text-gray-500">
                  No hotels found
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-50 to-blue-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Hotel Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Manager Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Credit Balance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {hotels.map((hotel, index) => (
                      <motion.tr
                        key={hotel._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`transition-colors duration-150 ${
                          index % 2 === 0 ? "bg-white" : "bg-indigo-50/30"
                        } hover:bg-indigo-100/30`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {hotel.hotelName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {hotel.city}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {hotel.location}
                          </div>
                          <div className="text-sm text-gray-500">
                            {hotel.state}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {hotel.mobileNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {hotel.manager ? (
                            <>
                              <div className="text-sm font-medium text-gray-900">
                                {hotel.manager.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {hotel.manager.email}
                              </div>
                              <div className="text-sm text-gray-500">
                                {hotel.manager.mobile}
                              </div>
                              <div className="text-sm text-gray-500">
                                Receptionists: {hotel.manager.receptionistCount}
                              </div>
                            </>
                          ) : (
                            <span className="text-sm text-gray-400">
                              No manager
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <div
                              className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center space-x-1 ${
                                hotel.creditBalance < 1000
                                  ? "bg-red-100 text-red-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              <span>
                                {hotel.creditBalance?.toLocaleString() || "0"}
                              </span>
                              {hotel.creditBalance < 1000 && (
                                <span className="text-xs">⚠️</span>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() =>
                              toggleHotelStatus(hotel, !hotel.isActive)
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none  ${
                              hotel.isActive ? "bg-indigo-600" : "bg-gray-200"
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                hotel.isActive
                                  ? "translate-x-6"
                                  : "translate-x-1"
                              }`}
                            />
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() =>
                                handleInPersonLogin(hotel.manager._id)
                              }
                              className="text-green-600 hover:text-green-900 p-1"
                              title="In-Person Login"
                            >
                              <MdLogin className="h-6 w-6" />
                            </button>
                            <button
                              onClick={() => openEditModal(hotel)}
                              className="text-indigo-600 hover:text-indigo-900 p-1"
                              title="Edit"
                            >
                              <MdEdit className="h-6 w-6" />
                            </button>

                            <button
                              onClick={() => {
                                setselectedManager({ ...hotel.manager });
                                setShowResetModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Reset Password"
                            >
                              <MdLockReset className="h-6 w-6" />
                            </button>

                            <label
                              htmlFor={`file-upload-${hotel._id}`}
                              className="cursor-pointer text-purple-600 hover:text-purple-900 p-1"
                              title="Upload File"
                            >
                              <NavLink
                                to={`/upload-csv/${hotel._id}`}
                                state={{ hotelName: hotel.hotelName }}
                              >
                                <MdCloudUpload className="h-6 w-6" />
                              </NavLink>
                            </label>
                            <label
                              className="cursor-pointer text-green-600 hover:text-green-900 p-1"
                              title="Add Credit"
                            >
                              <NavLink
                                to={`/add-credit/${hotel._id}`}
                                state={{ hotelName: hotel.hotelName }}
                              >
                                <MdAddCard className="h-6 w-6" />
                              </NavLink>
                            </label>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
        {showModal && (
          <HotelFormModal
            showModal={showModal}
            isEditing={isEditing}
            setShowModal={setShowModal}
            handleResetPassword={handleResetPassword}
            fetchHotels={fetchHotels}
            hotelEditFormData={hotelEditFormData}
          />
        )}
        {showResetModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setShowResetModal(false);
              }}
            ></div>

            <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-yellow-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                  </div>

                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      Reset Password Confirmation
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You are about to reset the password for{" "}
                        <span className="font-medium text-gray-900">
                          {selectedManager?.name}
                        </span>
                        .
                      </p>
                      <p className="mt-2 text-sm text-gray-500">
                        The password will be set to:{" "}
                        <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">
                          Abc@123
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  onClick={() => {
                    handleResetPassword(selectedManager._id);
                    setShowResetModal(false);
                  }}
                  className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <svg
                    className="-ml-1 mr-2 h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Confirm Reset
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowResetModal(false);
                  }}
                  className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  <svg
                    className="-ml-1 mr-2 h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotelList;
